import { useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, MapPin, CalendarClock } from "lucide-react";
import { PRIORITY_META } from "@/lib/meta";
import { useStatuses } from "@/context/StatusContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { fmtDate, isPastDue } from "@/components/equipment/equipmentMeta";

export const ClaimJobDialog = ({ job, onClose, onClaimed }) => {
  const [busy, setBusy] = useState(false);
  const claim = async () => {
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/claim`);
      toast.success(`'${job.title}' is now assigned to you`);
      onClaimed(job);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      onClaimed(null);
    } finally {
      setBusy(false);
      onClose();
    }
  };
  return (
    <AlertDialog open={!!job} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent className="rounded-none" data-testid="claim-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-heading text-2xl font-black tracking-tighter">Assign this job to you?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="text-sm text-zinc-600 space-y-1">
              {job && (
                <>
                  <div className="font-bold text-zinc-900" data-testid="claim-confirm-title">{job.title}</div>
                  {job.customer && <div>{job.customer}</div>}
                  <div>{job.location}{job.due_date ? ` · due ${fmtDate(job.due_date)}` : ""}</div>
                  <p className="pt-2">Once assigned, only a manager can move it to someone else.</p>
                </>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none" data-testid="claim-cancel-button">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={claim} disabled={busy} data-testid="claim-confirm-button" className="rounded-none bg-brand hover:bg-brand-dark">
            {busy ? "Assigning…" : "Yes, assign to me"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const UnassignedJobs = ({ jobs, onClaimed, hasLocations }) => {
  const { metaFor } = useStatuses();
  const [claiming, setClaiming] = useState(null);
  if (!hasLocations) {
    return <div className="bg-white border border-zinc-200 px-5 py-10 text-center text-sm text-zinc-500" data-testid="unassigned-no-locations">You don't have any locations assigned yet. Ask an admin to add your locations to see unassigned jobs.</div>;
  }
  return (
    <div className="space-y-3" data-testid="unassigned-jobs-list">
      {jobs.length === 0 && <div className="bg-white border border-zinc-200 px-5 py-10 text-center text-sm text-zinc-400">No unassigned jobs at your locations right now.</div>}
      {jobs.map((j) => {
        const p = PRIORITY_META[j.priority] || PRIORITY_META.medium;
        const s = metaFor(j.status);
        return (
          <div key={j.id} data-testid={`unassigned-job-${j.id}`} className="bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] p-4 flex flex-wrap items-center gap-4">
            <Link to={`/jobs/${j.id}`} className="flex-1 min-w-[220px] group" data-testid={`unassigned-view-${j.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-heading text-lg font-bold tracking-tight text-zinc-900 group-hover:text-brand break-words">{j.title}</span>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border rounded-full ${s.cls}`}>{s.label}</span>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${p.cls}`}>{p.label}</span>
              </div>
              {j.customer && <div className="text-sm text-zinc-700">{j.customer}</div>}
              <div className="flex flex-wrap gap-x-4 text-xs text-zinc-500 mt-1">
                <span className="flex items-center gap-1"><MapPin size={12} /> {j.location}</span>
                {j.due_date && <span className={`flex items-center gap-1 ${isPastDue(j.due_date) ? "text-red-600 font-bold" : ""}`}><CalendarClock size={12} /> Due {fmtDate(j.due_date)}</span>}
              </div>
            </Link>
            <button onClick={() => setClaiming(j)} data-testid={`claim-job-${j.id}`}
              className="bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors px-4 py-2 text-sm flex items-center gap-2">
              <UserPlus size={16} /> Assign to me
            </button>
          </div>
        );
      })}
      <ClaimJobDialog job={claiming} onClose={() => setClaiming(null)} onClaimed={onClaimed} />
    </div>
  );
};
